// src/components/SEO.js
import React from 'react';
import { Helmet } from 'react-helmet';
import { useStaticQuery, graphql } from 'gatsby';
import { useI18next, useTranslation } from 'gatsby-plugin-react-i18next';

const SEO = ({ 
  title, 
  description, 
  pathname = '', 
  children,
  article = false,
  keywords = [],
  familyData = null, // For family-specific structured data
  noindex = false
}) => {
  const { language, languages } = useI18next();
  const { t } = useTranslation();
  
  const {
    site: {
      siteMetadata: {
        defaultTitle,
        defaultDescription,
        siteUrl,
        defaultImage,
        author,
        type,
        familyName,
        keywords: defaultKeywords = []
      },
    },
  } = useStaticQuery(graphql`
    query SEOQuery {
      site {
        siteMetadata {
          defaultTitle: title
          defaultDescription: description
          siteUrl
          defaultImage: image
          author
          type
          familyName
          keywords
        }
      }
    }
  `);

  // Handle family avatar image
  const image = familyData?.avatar 
    ? `${process.env.S3_BUCKET_URL}${familyData.avatar.replace('/thumb/', '/webview/')}`
    : `${siteUrl}${defaultImage}`;

  // Build the correct URL based on language
  const currentPath = pathname.startsWith('/') ? pathname : `/${pathname}`;
  const currentUrl = language === 'en' 
    ? `${siteUrl}${currentPath}` 
    : `${siteUrl}/${language}${currentPath}`;

  const seo = {
    title: title || defaultTitle,
    description: description || defaultDescription,
    image: image,
    url: currentUrl,
    keywords: [...defaultKeywords, ...keywords].filter(Boolean).join(', ')
  };

  // Generate hreflang URLs
  const getHreflangUrls = () => {
    const urls = {};
    languages.forEach(lang => {
      if (lang === 'en') {
        urls[lang] = `${siteUrl}${currentPath}`;
      } else {
        urls[lang] = `${siteUrl}/${lang}${currentPath}`;
      }
    });
    urls['x-default'] = `${siteUrl}${currentPath}`; // Default to English
    return urls;
  };

  const hreflangUrls = getHreflangUrls();

  // Get locale string for Open Graph
  const getLocaleString = (lang) => {
    const locales = {
      'en': 'en_US',
      'es': 'es_ES'
    };
    return locales[lang] || 'en_US';
  };

  // Get language display name
  const getLanguageDisplayName = (lang) => {
    return t(`seo.languages.${lang}`, { defaultValue: lang === 'en' ? 'English' : 'Spanish' });
  };

  // Generate structured data for adoption services with i18n support
  const getStructuredData = () => {
    const baseData = {
      "@context": "https://schema.org",
      "@graph": []
    };

    // Website/Organization data
    if (type === 'main-site') {
      baseData["@graph"].push({
        "@type": "Organization",
        "@id": `${siteUrl}/#organization`,
        "name": "ParentFinder",
        "url": siteUrl,
        "description": seo.description,
        "logo": {
          "@type": "ImageObject",
          "url": `${siteUrl}/images/logo.png`
        },
        "sameAs": [
          "https://www.facebook.com/parentfinder",
          "https://www.instagram.com/parentfinder"
        ],
        "serviceType": t('seo.serviceType'),
        "areaServed": t('seo.areaServed'),
        "availableLanguage": languages.map(lang => ({
          "@type": "Language",
          "name": getLanguageDisplayName(lang),
          "alternateName": lang
        }))
      });

      baseData["@graph"].push({
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        "url": siteUrl,
        "name": defaultTitle,
        "description": seo.description,
        "publisher": {
          "@id": `${siteUrl}/#organization`
        },
        "inLanguage": language,
        "availableLanguage": languages
      });
    } else if (type === 'family-site' && familyData) {
      // Family-specific structured data
      baseData["@graph"].push({
        "@type": "Person",
        "@id": `${siteUrl}/#family`,
        "name": familyName,
        "description": t('seo.familyDescription', { familyName }),
        "url": siteUrl,
        "image": familyData.avatar ? `${process.env.S3_BUCKET_URL}${familyData.avatar}` : null,
        "knowsAbout": [
          t('seo.knowledge.adoption'),
          t('seo.knowledge.parenting'),
          t('seo.knowledge.familyBuilding')
        ],
        "seeks": {
          "@type": "Thing",
          "name": t('seo.seeks.name'),
          "description": t('seo.seeks.description')
        },
        "speaksLanguage": languages.map(lang => ({
          "@type": "Language",
          "name": getLanguageDisplayName(lang),
          "alternateName": lang
        }))
      });

      baseData["@graph"].push({
        "@type": "WebSite",
        "@id": `${siteUrl}/#website`,
        "url": siteUrl,
        "name": seo.title,
        "description": seo.description,
        "about": {
          "@id": `${siteUrl}/#family`
        },
        "inLanguage": language,
        "availableLanguage": languages
      });
    }

    // Current page data
    baseData["@graph"].push({
      "@type": "WebPage",
      "@id": `${seo.url}/#webpage`,
      "url": seo.url,
      "name": seo.title,
      "description": seo.description,
      "inLanguage": language,
      "isPartOf": {
        "@id": `${siteUrl}/#website`
      },
      "dateModified": new Date().toISOString(),
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          {
            "@type": "ListItem",
            "position": 1,
            "name": t('navigation.home'),
            "item": language === 'en' ? siteUrl : `${siteUrl}/${language}`
          }
        ]
      }
    });

    return JSON.stringify(baseData);
  };

  return (
    <Helmet 
      title={seo.title}
      titleTemplate={type === 'family-site' ? `%s` : `%s | ParentFinder`}
    >
      {/* Basic Meta Tags */}
      <html lang={language} />
      <meta name="description" content={seo.description} />
      <meta name="image" content={seo.image} />
      {seo.keywords && <meta name="keywords" content={seo.keywords} />}
      <meta name="author" content={author} />
      
      {/* Language and Content */}
      <meta httpEquiv="content-language" content={language} />
      
      {/* Robots */}
      <meta name="robots" content={noindex ? "noindex, nofollow" : "index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1"} />
      <meta name="googlebot" content={noindex ? "noindex, nofollow" : "index, follow"} />
      
      {/* Viewport */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0, maximum-scale=5.0" />
      
      {/* Canonical */}
      <link rel="canonical" href={seo.url} />
      
      {/* Hreflang tags */}
      {Object.entries(hreflangUrls).map(([lang, url]) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={url} />
      ))}
      
      {/* Open Graph */}
      <meta property="og:url" content={seo.url} />
      <meta property="og:type" content={article ? 'article' : 'website'} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={seo.title} />
      <meta property="og:site_name" content={type === 'main-site' ? 'ParentFinder' : `${familyName} ${t('seo.familySiteName')}`} />
      <meta property="og:locale" content={getLocaleString(language)} />
      
      {/* Alternate locale tags */}
      {languages.filter(lang => lang !== language).map(lang => (
        <meta key={lang} property="og:locale:alternate" content={getLocaleString(lang)} />
      ))}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      <meta name="twitter:image:alt" content={seo.title} />
      
      {/* Additional SEO */}
      <meta name="theme-color" content="#9bd45a" />
      <meta name="msapplication-TileColor" content="#9bd45a" />
      
      {/* PWA Language Support */}
      <meta name="apple-mobile-web-app-title" content={seo.title} />
      <meta name="application-name" content={seo.title} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {getStructuredData()}
      </script>
      
      {children}
    </Helmet>
  );
};

export default SEO;